import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

const SITE = "https://capitalpropressurewashing.com";

export type Crumb = { label: string; href?: string };

/**
 * Visible breadcrumb trail + matching BreadcrumbList JSON-LD.
 * The last crumb is the current page and is not linked.
 */
export function Breadcrumbs({
  items,
  homeLabel = "Home",
  className = "",
}: {
  items: Crumb[];
  homeLabel?: string;
  className?: string;
}) {
  const all: Crumb[] = [{ label: homeLabel, href: "/" }, ...items];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: all.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.label,
      // The current (last) crumb has no href — omit `item` rather than
      // falling back to the homepage URL, which is an invalid trail.
      ...(c.href ? { item: `${SITE}${c.href}` } : {}),
    })),
  };

  return (
    <nav
      aria-label="Breadcrumb"
      className={`bg-[var(--brand-light-gray)] border-b border-gray-200 ${className}`}
    >
      <div className="mx-auto max-w-[1200px] px-4 py-3 text-sm text-[var(--brand-charcoal)]/70">
        <ol className="flex flex-wrap items-center gap-2">
          {all.map((c, i) => {
            const last = i === all.length - 1;
            return (
              <li key={`${c.label}-${i}`} className="flex items-center gap-2">
                {i > 0 && <ChevronRight size={14} aria-hidden="true" />}
                {last || !c.href ? (
                  <span
                    className="text-[var(--brand-deep-blue)] font-semibold"
                    aria-current={last ? "page" : undefined}
                  >
                    {c.label}
                  </span>
                ) : (
                  <Link to={c.href} className="hover:text-[var(--brand-deep-blue)]">
                    {c.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </nav>
  );
}
